import { useState, useRef, useEffect, cloneElement, useCallback, ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { cn } from "@/lib/utils";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  computePosition,
  Placement,
  autoPlacement,
  hide,
  limitShift,
  size,
} from '@floating-ui/react-dom';

interface HighlightTooltipProps {
  content: React.ReactNode;
  children: ReactElement;
  className?: string;
  position?: Placement;
  offsetDistance?: number;
  maxWidth?: number;
  delayShow?: number;
  delayHide?: number;
  showArrow?: boolean;
}

/**
 * Enhanced tooltip component for highlight notes
 * Features:
 * - Uses Floating UI for sophisticated positioning and collision detection
 * - Rich content support (HTML, formatting, etc.)
 * - Supports 12 different placement options (4 sides + 8 corners)
 * - Automatically adjusts position to stay within viewport
 * - Optional arrow indicator pointing to trigger element
 * - Show/hide delay for better UX
 * - Keyboard accessibility
 * - Works with both hover and focus events
 * - Smooth animation for entrance/exit
 * - Recalculates on scroll and resize
 */
export const HighlightTooltip = ({
  content,
  children,
  className,
  position = 'top',
  offsetDistance = 8,
  maxWidth = 300,
  delayShow = 200,
  delayHide = 150,
  showArrow = true,
}: HighlightTooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const arrowRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<number | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);
  
  // Set up Floating UI
  const {
    x,
    y,
    strategy,
    refs,
    placement,
    middlewareData,
    update
  } = useFloating({
    placement: position,
    middleware: [
      offset(offsetDistance),
      flip({ padding: 10 }),
      shift({ padding: 10, limiter: limitShift() }),
      arrow({ element: arrowRef }),
      size({
        apply({ availableWidth, availableHeight, elements }) {
          // Apply max width constraints based on available space
          Object.assign(elements.floating.style, {
            maxWidth: `${Math.min(maxWidth, availableWidth - 20)}px`,
            maxHeight: `${Math.min(500, availableHeight - 40)}px`,
            overflow: 'auto'
          });
        },
        padding: 10
      })
    ],
    whileElementsMounted: autoUpdate
  });
  
  // Get arrow position and rotation based on placement
  const getArrowStyles = () => {
    if (!showArrow || !middlewareData.arrow) return {};
    
    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right'
    }[placement.split('-')[0]];
    
    const arrowX = middlewareData.arrow?.x;
    const arrowY = middlewareData.arrow?.y;
    
    return {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide as string]: '-4px',
      transform: {
        top: 'rotate(45deg)',
        right: 'rotate(135deg)',
        bottom: 'rotate(225deg)',
        left: 'rotate(315deg)'
      }[placement.split('-')[0]]
    };
  };
  
  // Show tooltip after delay
  const showTooltip = useCallback(() => {
    if (showTimeoutRef.current) {
      window.clearTimeout(showTimeoutRef.current);
    }
    
    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    
    showTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(true);
      showTimeoutRef.current = null;
    }, delayShow);
  }, [delayShow]);
  
  // Hide tooltip after delay
  const hideTooltip = useCallback(() => {
    if (showTimeoutRef.current) {
      window.clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    
    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
    }
    
    hideTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(false);
      hideTimeoutRef.current = null;
    }, delayHide);
  }, [delayHide]);
  
  // Update positioning when tooltip becomes visible
  useEffect(() => {
    if (isVisible) {
      // Force an update to ensure correct positioning
      update();
    }
  }, [isVisible, update]);
  
  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) {
        window.clearTimeout(showTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);
  
  // Create event handlers
  const handleMouseEnter = useCallback((e: React.MouseEvent) => {
    showTooltip();
    if (children.props.onMouseEnter) {
      children.props.onMouseEnter(e);
    }
  }, [children.props.onMouseEnter, showTooltip]);
  
  const handleMouseLeave = useCallback((e: React.MouseEvent) => {
    hideTooltip();
    if (children.props.onMouseLeave) {
      children.props.onMouseLeave(e);
    }
  }, [children.props.onMouseLeave, hideTooltip]);
  
  const handleFocus = useCallback((e: React.FocusEvent) => {
    showTooltip();
    if (children.props.onFocus) {
      children.props.onFocus(e);
    }
  }, [children.props.onFocus, showTooltip]);
  
  const handleBlur = useCallback((e: React.FocusEvent) => {
    hideTooltip();
    if (children.props.onBlur) {
      children.props.onBlur(e);
    }
  }, [children.props.onBlur, hideTooltip]);
  
  // Clone child element to add refs and event handlers
  const enhancedTrigger = cloneElement(children, {
    ref: refs.setReference,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
  });
  
  return (
    <>
      {enhancedTrigger}
      {isVisible && createPortal(
        <div
          ref={refs.setFloating}
          role="tooltip"
          className={cn(
            "fixed z-50 px-3.5 py-2.5 text-sm rounded-lg border shadow-lg",
            "border-border/50 bg-background/95 backdrop-blur-sm",
            "text-left text-foreground animate-in fade-in-50 zoom-in-95",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "data-[placement=top]:slide-in-from-bottom-2",
            "data-[placement=bottom]:slide-in-from-top-2",
            "data-[placement=left]:slide-in-from-right-2",
            "data-[placement=right]:slide-in-from-left-2",
            className
          )}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            maxWidth: `${maxWidth}px`,
            transformOrigin: 'center center',
            overflowY: 'auto'
          }}
          data-placement={placement}
          data-state={isVisible ? 'open' : 'closed'}
          onMouseEnter={() => {
            // Keep tooltip visible while mouse is over it
            if (hideTimeoutRef.current) {
              window.clearTimeout(hideTimeoutRef.current);
              hideTimeoutRef.current = null;
            }
          }}
          onMouseLeave={hideTooltip}
        >
          {showArrow && (
            <div
              ref={arrowRef}
              className="absolute w-2 h-2 bg-background border-l border-t border-border/50 rotate-45"
              style={getArrowStyles()}
            />
          )}
          {content}
        </div>,
        document.body
      )}
    </>
  );
}; 